/**
 * Map Overlay Control Styles
 *
 * Consistent styling for all floating controls on the status map.
 * Following FarmHQ design system with subtle, functional aesthetics.
 */
import { StyleSheet } from "react-native"

import { COLORS } from "@/theme/theme/colors"
import { RADII } from "@/theme/theme/radii"
import { SHADOWS } from "@/theme/theme/shadows"
import { SPACING } from "@/theme/theme/spacing"
import { useIsDarkMode } from "@/theme"

/**
 * Shared dimensions and values for map overlay controls
 */
export const MAP_OVERLAY_TOKENS = {
  // Touch target size (44px minimum for accessibility)
  buttonSize: 44,
  // Consistent border radius - larger for modern look
  borderRadius: RADII.$xl,
  // Border width for subtle definition
  borderWidth: 0.5,
  // Padding for icon buttons
  iconPadding: SPACING.$2,
  // Shadow level (subtle lift)
  shadowLevel: SHADOWS.$3,
  // Background opacity for translucent effect over the map
  backgroundOpacity: 0.92,
}

/**
 * Hook to get theme-aware map overlay styles
 */
export function useMapOverlayStyle() {
  const isDark = useIsDarkMode()

  // Semi-transparent backgrounds for glassmorphic effect over the map
  const backgroundColor = isDark
    ? `rgba(39, 39, 42, ${MAP_OVERLAY_TOKENS.backgroundOpacity})`  // gray[800] with opacity
    : `rgba(255, 255, 255, ${MAP_OVERLAY_TOKENS.backgroundOpacity})`

  return {
    // Surface colors - semi-transparent for map overlay effect
    backgroundColor,
    borderColor: isDark
      ? "rgba(255, 255, 255, 0.12)"
      : "rgba(0, 0, 0, 0.06)",
    // Icon/text colors
    iconColor: isDark ? COLORS.$icon.dark : COLORS.$icon.light,
    iconColorActive: isDark ? COLORS.$textLight.primary : COLORS.$textDark.primary,
    // Pressed state - slightly more opaque when pressed
    pressedBackgroundColor: isDark
      ? "rgba(63, 63, 70, 0.95)"  // gray[700]
      : "rgba(244, 244, 245, 0.95)",  // gray[100]
  }
}

/**
 * Static styles for map overlay controls
 */
export const MAP_OVERLAY_STYLES = StyleSheet.create({
  // Base button container
  buttonBase: {
    alignItems: "center",
    borderRadius: MAP_OVERLAY_TOKENS.borderRadius,
    borderWidth: MAP_OVERLAY_TOKENS.borderWidth,
    height: MAP_OVERLAY_TOKENS.buttonSize,
    justifyContent: "center",
    minWidth: MAP_OVERLAY_TOKENS.buttonSize,
    ...MAP_OVERLAY_TOKENS.shadowLevel,
  },
  // Icon-only button (square)
  iconButton: {
    width: MAP_OVERLAY_TOKENS.buttonSize,
  },
  // Button with text (pill shape)
  pillButton: {
    flexDirection: "row",
    gap: SPACING.$1,
    paddingHorizontal: SPACING.$3,
  },
  // Pressed state transform
  pressed: {
    transform: [{ scale: 0.97 }],
  },
})
